/* eslint-disable @typescript-eslint/no-non-null-assertion */
import * as Communicator from "@ts3d-hoops/web-viewer";
import {
    StreamApi,
    DataCreateStream,
    ResponseCreateStream,
} from "api/stream.api";
import { FileInfo, FlatTreeNode, Views } from "common/define";
import { GlobalState } from "common/global";
import { SystemConstants } from "common/system-constants";
import { CuttingPlaneState, NavOperatorState } from "common/type-state";
import { CuttingType, OperatorType, PlaneBoxChild } from "common/type-viewer";
import { selectionFlag } from "container/viewer3d/extends/CSelectionManager";
import { MarkupAction } from "container/viewer3d/markup/markup.action";
import { Observable } from "rxjs";
import { switchMap } from "rxjs/operators";
import Utils, { Lodash } from "utils/utils";
import _ from "lodash";
import WebViewerExtend from "container/viewer3d/extends/webviewer.extend";
import CSelectionOperator from "container/viewer3d/extends/CSelectionOperator";
import CTurnTableOperator from "container/viewer3d/extends/CTurnTableOperator";
import CPanOperator from "container/viewer3d/extends/CPanOperator";
import CZoomOperator from "container/viewer3d/extends/CZoomOperator";

/** interface */
export interface Viewer3DState {
    operator: {
        [viewId: string]: NavOperatorState;
    };
    cuttingPlane: {
        [viewId: string]: CuttingPlaneState;
    };
    planeBoxChildState: {
        [viewId: string]: {
            planeBoxChild: PlaneBoxChild[];
            isClipping: boolean;
        };
    };
    loadingViewer: {
        [viewId: string]: boolean;
    };
    webViewerState: {
        [viewId: string]: boolean;
    };
    modelStructureReady: {
        [viewId: string]: boolean;
    };
    selectionItem: {
        [viewId: string]: {
            nodes: NodeId[];
            type: SelectedNodeType;
        };
    };
    hidedItem: {
        [viewId: string]: NodeId[];
    };
    removeAllHidedItem: boolean;
}
export interface PayloadCreateWebViewer {
    response: ResponseCreateStream;
    containerId: string;
    modelName: string;
    viewId: string;
}
export interface MapViewsItem {
    views: Views;
    viewId: string;
}
interface ResultExplodeState {
    currentValueExplode?: number;
    currentOperator?: OperatorType[];
}
export interface ResultInitOperatorState {
    viewId?: ViewId;
    operatorState: NavOperatorState;
    cuttingPlaneState: CuttingPlaneState;
}
export interface ResultResetOperatorState {
    operatorState: NavOperatorState;
    viewId?: ViewId;
}

export type PersistenIdOfNode = {
    _nodeId: number;
    _persistentId: string | undefined;
};
/** function */
export const DefaultDisableCutting: CuttingType[] = [
    "reverse-plane-x",
    "reverse-plane-y",
    "reverse-plane-z",
    "toggle-plane",
    "toggle-section",
    "rotate-box",
];
function createWebViewerFunc({
    response,
    containerId,
    modelName,
    viewId,
}: PayloadCreateWebViewer): {
    viewId: ViewId;
    viewer: Communicator.IWebViewer;
} {
    const viewer = new WebViewerExtend({
        model: modelName,
        containerId: containerId,
        endpointUri: response.endpoints.ws,
        calculateDefaultViewAxes: false,
        streamingMode: Communicator.StreamingMode.Interactive,
        // HOOPS 2026.3.0: Use absolute path to ensure WASM/MJS modules load correctly regardless of current route
        enginePath: `${
            window.location.origin
        }${Utils.getPublicPath()}/assets/hoops`,
    });
    return { viewId, viewer };
}
function createDataStream(location: string): DataCreateStream {
    return {
        class: SystemConstants.RENDER,
        params: {
            modelSearchDirectories: [location],
        },
    };
}
export function UnHightLightFaceLightPoint(
    webViewerActive: Communicator.IWebViewer
): void {
    if (webViewerActive) {
        if (webViewerActive.selectionManager.getHighlightFaceElementSelection())
            webViewerActive.selectionManager.setHighlightFaceElementSelection(
                false
            );
        if (webViewerActive.selectionManager.getHighlightLineElementSelection())
            webViewerActive.selectionManager.setHighlightLineElementSelection(
                false
            );
        if (
            webViewerActive.selectionManager.getHighlightPointElementSelection()
        )
            webViewerActive.selectionManager.setHighlightPointElementSelection(
                false
            );
    }
}
/** export functions, variable */
export function GetNodeInTreePerNode(
    webViewerActive: Communicator.IWebViewer,
    modelFileId: string,
    selectionArray: number[],
    treeMapData: Map<string, FlatTreeNode>
): Map<number, number> {
    const mapRet = new Map<number, number>();
    const arrNodeParentSelect = getParentNodeOfArrNode(
        selectionArray,
        webViewerActive,
        100
    );
    arrNodeParentSelect.forEach((listNodes) => {
        const nodeInTree = listNodes.find((nodeId) =>
            treeMapData.has(String(nodeId))
        );
        if (nodeInTree !== undefined) {
            mapRet.set(listNodes[0], nodeInTree);
        }
    });
    return mapRet;
}
export function UnSelectNodes(
    webViewer: Communicator.IWebViewer,
    nodes: number[]
): void {
    const selectionManager = webViewer.selectionManager;
    const nodeSelectionItems = selectionManager.getResults();
    nodeSelectionItems.forEach((nodeSelectionItem) => {
        if (nodes.includes(nodeSelectionItem.getNodeId())) {
            selectionManager.remove(nodeSelectionItem);
        }
    });
    // fix unselect node in model tree when draw mode is Ghost
    // if (webViewer.view.getDrawMode() === Communicator.DrawMode.Ghost) {
    //     ModelHelper.setSelectionGhostMode(webViewer);
    // }
    // webViewer.redraw();
}

export function getPersistentIdsMapInNodeIds(
    viewActive: Communicator.IWebViewer,
    viewId: string,
    selectionNodeIds: number[]
): Map<number, PersistenIdOfNode[]> {
    const persistentMapRet: Map<number, PersistenIdOfNode[]> = new Map<
        number,
        PersistenIdOfNode[]
    >();
    const arrNodeParentSelect = getParentNodeOfArrNode(
        selectionNodeIds,
        viewActive,
        100
    );
    for (let index = 0; index < arrNodeParentSelect.length; index++) {
        const nodeParentIDs = arrNodeParentSelect[index];
        const basenode = nodeParentIDs[0];

        const listPersistentId: PersistenIdOfNode[] = [];
        for (let index = 0; index < nodeParentIDs.length; index++) {
            const nodeID = nodeParentIDs[index];
            const persistentID = GlobalState.GetPesistentsIdByNodeIds(
                viewId,
                nodeID
            );
            listPersistentId.push({
                _nodeId: nodeID,
                _persistentId: persistentID,
            });
        }
        persistentMapRet.set(basenode, listPersistentId);
    }
    return persistentMapRet;
}

export function getNodeIdsByPersistentIds(
    webViewer: Communicator.IWebViewer,
    viewId: ViewId,
    persistentIds: string[]
): number[] {
    const nodeIds: number[] = [];
    for (let index = 0; index < persistentIds.length; index++) {
        const persistentId = persistentIds[index];
        const nodeIdsInSheet = GlobalState.getNodeidsByPersistanceId(
            webViewer,
            viewId,
            persistentId
        );
        if (nodeIdsInSheet) {
            for (let index2 = 0; index2 < nodeIdsInSheet.length; index2++) {
                const nodeIdInSheet = nodeIdsInSheet[index2];
                const type = webViewer.model.getNodeType(nodeIdInSheet);
                if (type !== Communicator.NodeType.AssemblyNode) {
                    nodeIds.push(nodeIdInSheet);
                }
            }
        }
    }
    return nodeIds;
}
// On drawing sheets, model tree node ids are not always the rendered HOOPS nodes.
// Use PersistentId to find selectable/highlightable sheet nodes for the active view.
export function getSheetSelectableNodeIdsByTreeNodes(
    webViewer: Communicator.IWebViewer,
    viewId: ViewId,
    treeNodes: FlatTreeNode[]
): number[] {
    const nodeIdSet = new Set<number>();
    treeNodes.forEach((treeNode) => {
        if (!treeNode.PersistentId) return;
        const nodeIdsInSheet = getNodeIdsByPersistentIds(webViewer, viewId, [
            treeNode.PersistentId,
        ]);
        nodeIdsInSheet.forEach((nodeIdInSheet) => {
            if (
                Number.isFinite(nodeIdInSheet) &&
                webViewer.model.getNodeType(nodeIdInSheet) !==
                    Communicator.NodeType.AssemblyNode
            ) {
                nodeIdSet.add(nodeIdInSheet);
            }
        });
    });
    return Array.from(nodeIdSet);
}
export function Update3dViewerSelection(
    webViewer: Communicator.IWebViewer,
    selectionArray: number[]
): void {
    const curSelections = webViewer.selectionManager
        .getResults()
        .map((node) => node.getNodeId());
    const removeIds = _.difference(curSelections, selectionArray); //curSelections.filter(node => !selectionArray.includes(node));
    const addIds = _.difference(selectionArray, curSelections); //selectionArray.filter(node => !curSelections.includes(node));
    if (removeIds.length > 0) UnSelectNodes(webViewer, removeIds);
    if (addIds.length > 0) SelectNodesWithTriggerFlag(webViewer, addIds, false);
}
export function ClearSelectNodesWithoutTrigger(
    webViewer: Communicator.IWebViewer
): void {
    const selectionManager = webViewer.selectionManager;
    selectionManager.clear(false);
}

export async function SelectNodesWithTriggerFlag(
    webViewer: Communicator.IWebViewer,
    nodes: number[],
    triggerSelection = true
): Promise<void> {
    return new Promise((resolve) => {
        const selectionManager = webViewer.selectionManager;
        selectionManager.addFlag(selectionFlag.SelectByCode);
        SelectNodes(webViewer, nodes, triggerSelection);
        selectionManager.removeFlag(selectionFlag.SelectByCode);
        webViewer.redraw();
    });
}

export function SelectNodes(
    webViewer: Communicator.IWebViewer,
    nodes: number[],
    triggerSelection: boolean
): void {
    const selectionManager = webViewer.selectionManager;
    const selections = selectionManager
        .getResults()
        .map((node) => node.getNodeId());
    // nodes.forEach(node => {
    //     if (!selections.includes(node)) {
    //         selectionManager.selectNode(node, Communicator.SelectionMode.Add);
    //         webViewer.redraw();
    //     }
    // });
    // const selectedNodes = nodes.filter(id => ![ Communicator.NodeType.AssemblyNode, Communicator.NodeType.Unknown ].includes(webViewer.model.getNodeType(id)));
    const diff = _.difference(nodes, selections);
    let resultSelected: Communicator.SelectionType | null = null;
    diff.forEach((node) => {
        const res: Communicator.SelectionType = selectionManager.selectNode(
            node,
            Communicator.SelectionMode.Add
        );
        if (resultSelected === null && res === Communicator.SelectionType.None)
            resultSelected = res;
    });
    // select a node not shown on model => alert a tooltip for user
    if (resultSelected === null) return;
    GlobalState.tooltipNodeSelection$.next(true);
}
export function setNodesHighlighted(
    webViewer: Communicator.IWebViewer,
    nodes: number[]
): void {
    try {
        webViewer.model.setNodesHighlighted(
            [webViewer.model.getAbsoluteRootNode()],
            false
        );
        const nodesNumber = nodes.filter((num) => !isNaN(num));
        if (nodesNumber.length > 0) {
            webViewer.model.setNodesHighlighted(nodesNumber, true);
            const selectionManager = webViewer.selectionManager;
            selectionManager.addNodeSelectWithoutTrigger(nodesNumber);
        }
    } catch (error) {
        console.info(error);
    }
}
export function unsetNodesHighlighted(
    webViewer: Communicator.IWebViewer,
    nodes: number[]
): void {
    try {
        const nodesNumber = nodes.filter((num) => !isNaN(num));
        if (nodesNumber.length > 0) {
            webViewer.model.setNodesHighlighted(nodesNumber, false);
        }
        const selectionManager = webViewer.selectionManager;
        selectionManager.addNodeSelectWithoutTrigger([]);
    } catch (error) {
        console.info(error);
    }
}
export function createDataStream$(
    fileInfo: FileInfo,
    containerId: string
): Observable<string> {
    const locationTrim = Utils.regexStreamLocation(fileInfo.streamLocation);
    const data = createDataStream(locationTrim);
    return StreamApi.createStream(data).pipe(
        switchMap((response: ResponseCreateStream) => {
            GlobalState.rootStreamLocation = {
                location: locationTrim,
                response: response,
            };
            return createWebViewer$(fileInfo, response, containerId);
        })
    );
}

export function createWebViewer$(
    fileInfo: FileInfo,
    response: ResponseCreateStream,
    containerId: string
): Observable<string> {
    const modelName = Utils.getModelNameByStreamLocation(fileInfo);
    const payload: PayloadCreateWebViewer = {
        response,
        containerId,
        modelName,
        viewId: fileInfo.viewId,
    };
    const { viewId, viewer } = createWebViewerFunc(payload);
    GlobalState.map3DViewer.set(viewId, viewer);
    const markupAction = new MarkupAction(viewer);
    markupAction.modelFileId = fileInfo.modelFileId;
    GlobalState.markupActionMap.set(viewId, markupAction);
    console.info(`Viewer created: `, viewer);
    return new Observable<string>((obser) => {
        let started = false;
        let cancelled = false;
        let startPollTimeout: ReturnType<typeof setTimeout> | null = null;

        const shouldAbortStart = (): boolean => {
            if (cancelled) return true;
            const activeViewer = GlobalState.map3DViewer.get(viewId);
            return activeViewer !== viewer;
        };

        const startViewerWhenContainerReady = () => {
            if (started || shouldAbortStart()) return;
            const container = document.getElementById(containerId);
            const canStart =
                !!container &&
                container.clientWidth > 0 &&
                container.clientHeight > 0;
            if (canStart) {
                started = true;
                try {
                    viewer.start();
                } catch (error) {
                    console.warn("skip viewer.start after shutdown/dispose", {
                        viewId,
                        error,
                    });
                }
                return;
            }
            startPollTimeout = setTimeout(startViewerWhenContainerReady, 50);
        };

        const callbacks: Communicator.CallbackMap = {
            sceneReady: () => {
                if (shouldAbortStart()) return;
                console.info("sceneReady");
                // HOOPS 2026.3.0: Operators must be replaced only after the scene is ready
                // to ensure the internal ScEngine and Canvas are fully initialized.
                replaceOperatorsAfterViewerCreation(viewer);
            },
            assemblyTreeReady: () => {
                if (shouldAbortStart()) return;
                console.info("assemblyTreeReady");
                obser.next(viewId);
                obser.complete();
            },
            modelLoadFailure: (name, reason, error) => {
                if (shouldAbortStart()) return;
                console.error("modelLoadFailure", reason, error);
            },
        };

        viewer.setCallbacks(callbacks);
        // HOOPS 2026.3.0: Start the viewer AFTER setting callbacks
        // to catch all early initialization events correctly.
        startViewerWhenContainerReady();

        return () => {
            cancelled = true;
            if (startPollTimeout) {
                clearTimeout(startPollTimeout);
                startPollTimeout = null;
            }
            try {
                viewer.unsetCallbacks(callbacks);
            } catch (_error) {
                // Ignore teardown race with already disposed viewer.
            }
        };
    });
}

/**
 * Replace default HOOPS operators with custom implementations after viewer creation
 * In HOOPS 2025.1.1+, Communicator.Operators is read-only, so we replace instances using operatorManager
 */
function replaceOperatorsAfterViewerCreation(
    viewer: Communicator.IWebViewer
): void {
    try {
        console.info(
            "Replacing HOOPS operators with custom implementations after viewer creation"
        );
        // In HOOPS 2025.8.0, Navigate internally routes: orbit → zoom → pan.
        // Zoom and Pan both default to no-mapping (match all buttons), so Zoom always wins.
        // Disable the Navigate-internal P0 and D0 instances BEFORE replacing them,
        // so CPanOperator (on the stack) handles pan without interference.
        viewer.view.operatorManager
            .getOperator(Communicator.OperatorId.Select)
            .setMapping(Communicator.Button.None);
        viewer.view.operatorManager
            .getOperator(Communicator.OperatorId.Turntable)
            .setMapping(Communicator.Button.None);
        viewer.view.operatorManager
            .getOperator(Communicator.OperatorId.Pan)
            .setMapping(Communicator.Button.None);
        viewer.view.operatorManager
            .getOperator(Communicator.OperatorId.Zoom)
            .setMapping(Communicator.Button.None);
        // Replace SelectionOperator
        const customSelectionOperator = new CSelectionOperator(viewer);
        const selectionOperatorId =
            viewer.view.operatorManager.registerCustomOperator(
                customSelectionOperator
            );
        viewer.view.operatorManager.replaceOperator(
            Communicator.OperatorId.Select,
            selectionOperatorId
        );

        // Replace CameraTurntableOperator
        const customCameraTurntableOperator = new CTurnTableOperator(viewer);
        const cameraTurntableOperatorId =
            viewer.view.operatorManager.registerCustomOperator(
                customCameraTurntableOperator
            );
        viewer.view.operatorManager.replaceOperator(
            Communicator.OperatorId.Turntable,
            cameraTurntableOperatorId
        );

        // Replace CameraPanOperator
        const customCameraPanOperator = new CPanOperator(viewer);
        const cameraPanOperatorId =
            viewer.view.operatorManager.registerCustomOperator(
                customCameraPanOperator
            );
        viewer.view.operatorManager.replaceOperator(
            Communicator.OperatorId.Pan,
            cameraPanOperatorId
        );

        // Replace CameraZoomOperator
        const customCameraZoomOperator = new CZoomOperator(viewer);
        const cameraZoomOperatorId =
            viewer.view.operatorManager.registerCustomOperator(
                customCameraZoomOperator
            );
        viewer.view.operatorManager.replaceOperator(
            Communicator.OperatorId.Zoom,
            cameraZoomOperatorId
        );
        // In HOOPS 2025.8.0, CameraNavigationOperator holds a direct reference to the zoom operator
        // passed in its constructor. replaceOperator() only updates the OperatorManager slot,
        // not Navigate's internal _zoomOperator reference. Patch it directly so mousewheel
        // events route to our CZoomOperator.
        const navigateOperator = viewer.view.operatorManager.getOperator(
            Communicator.OperatorId.Navigate
        ) as unknown as { _zoomOperator?: CZoomOperator };
        if (navigateOperator) {
            navigateOperator._zoomOperator = customCameraZoomOperator;
        }

        console.info(
            "Successfully replaced HOOPS operators with custom implementations"
        );
    } catch (error) {
        console.warn(
            "Failed to replace operators after viewer creation:",
            error
        );
    }
}
export function setExplodeState(
    value: number,
    stateOperator: NavOperatorState
): ResultExplodeState {
    const result: ResultExplodeState = {};
    result.currentValueExplode = value;
    const currentStateOperator = [...stateOperator.currentOperator];
    if (value > 0) {
        if (currentStateOperator) {
            if (!currentStateOperator.includes("explode")) {
                const finalOperator = new Set<OperatorType>([
                    ...currentStateOperator,
                    "explode",
                ]);
                result.currentOperator = Array.from(finalOperator);
            }
        }
    } else {
        result.currentOperator = Lodash.remove(
            currentStateOperator,
            (op) => op !== "explode"
        );
    }
    return result;
}

export function updateCuttingPlaneState(
    cuttingState: CuttingPlaneState,
    currentOperator: OperatorType[]
): OperatorType[] | undefined {
    const plane: CuttingType[] = ["plane-x", "plane-y", "plane-z", "plane-box"];
    let result: OperatorType[] | undefined = undefined;
    const cloneCurrentOperator = [...currentOperator];
    if (cuttingState.active?.find((c) => plane.includes(c))) {
        if (!currentOperator.includes("cutting-plane")) {
            result = [...cloneCurrentOperator, "cutting-plane"];
        }
    } else {
        if (currentOperator.includes("cutting-plane")) {
            result = Lodash.remove(
                cloneCurrentOperator,
                (c) => c !== "cutting-plane"
            );
        }
    }
    return result;
}
export function initOperatorStateObser(
    isInViewOnly: boolean
): Observable<ResultInitOperatorState> {
    return new Observable<ResultInitOperatorState>((obser) => {
        const operatorState: NavOperatorState = {
            currentOperator: ["select", "drawMode-shaded"],
            listOperatorDisable: isInViewOnly ? ["save"] : [],
        };
        const cuttingState: CuttingPlaneState = {
            disable: DefaultDisableCutting,
        };
        obser.next({
            operatorState,
            cuttingPlaneState: cuttingState,
        });
        obser.complete();
    });
}
export function resetOperatorStateObser(
    isInViewOnly: boolean
): Observable<ResultResetOperatorState> {
    return new Observable<ResultResetOperatorState>((obser) => {
        const operatorState: NavOperatorState = {
            currentOperator: ["select", "drawMode-shaded-wire"],
            listOperatorDisable: isInViewOnly ? ["save"] : [],
        };
        obser.next({ operatorState });
        obser.complete();
    });
}
export function removeItemInArrHide(
    currentItems: NodeId[],
    arrRemove: NodeId[]
): NodeId[] {
    const cloneCurrentItems = [...currentItems];
    const cloneArrRemove = [...arrRemove];
    return Lodash.remove(cloneCurrentItems, (f) => !cloneArrRemove.includes(f));
}

export function getParentNodeOfLastNode(
    nodes: number[],
    viewer: Communicator.IWebViewer,
    numberGet = 2
): number[] {
    const lastNode = nodes[nodes.length - 1];
    if (lastNode) {
        const arrResult = [lastNode];
        let currNode = lastNode;
        for (let i = 0; i < numberGet; i++) {
            const parent = viewer.model.getNodeParent(currNode);
            if (parent) {
                arrResult.push(parent);
                currNode = parent;
            }
        }
        return arrResult;
    }
    return [];
}
export function getParentNodeOfArrNode(
    nodes: number[],
    viewer: Communicator.IWebViewer,
    numberGet = 2
): number[][] {
    if (nodes.length > 0) {
        const result = nodes.reduce<number[][]>((re, curr) => {
            let currNode = curr;
            const arr = [curr];
            for (let i = 0; i < numberGet; i++) {
                const parent = viewer.model.getNodeParent(currNode);
                if (parent) {
                    arr.push(parent);
                    currNode = parent;
                }
            }
            arr.length > 0 && re.push(arr);
            return re;
        }, []);
        return result;
    }
    return [];
}
